import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Loader2, Package } from 'lucide-react';
import { ModuleWithStatus } from '@/types/marketplace';
import * as LucideIcons from 'lucide-react';

interface ModuleDeactivationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: ModuleWithStatus | null;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

const ModuleDeactivationDialog = ({
  open,
  onOpenChange,
  module,
  onConfirm,
  isLoading,
}: ModuleDeactivationDialogProps) => {
  const [confirmed, setConfirmed] = useState(false);

  const handleOpenChange = (value: boolean) => {
    if (!value) setConfirmed(false);
    onOpenChange(value);
  };

  const handleConfirm = async () => {
    await onConfirm();
    setConfirmed(false);
    onOpenChange(false);
  };

  if (!module) return null;

  const IconComponent = module.icon
    ? (LucideIcons as any)[module.icon] || Package
    : Package;

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Desativar Módulo
          </DialogTitle>
          <DialogDescription>
            Confirme o agendamento de desativação do módulo abaixo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
            <div className="p-2 rounded-lg bg-background">
              <IconComponent className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium text-sm">{module.name}</p>
              <p className="text-xs text-muted-foreground">{formatPrice(module.price)}/lic/mês</p>
            </div>
          </div>

          <div className="rounded-lg border border-yellow-500/30 bg-yellow-50/50 dark:bg-yellow-950/10 p-3 space-y-2">
            <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">Como funciona:</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>O módulo permanecerá <strong>ativo até o final do período de faturamento atual</strong>.</li>
              <li>Após essa data, o acesso será removido automaticamente.</li>
              <li>O módulo não será cobrado na próxima fatura.</li>
              <li>Você poderá cancelar a desativação a qualquer momento antes do fim do período.</li>
            </ul>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="confirm-deactivation"
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked === true)}
            />
            <label
              htmlFor="confirm-deactivation"
              className="text-sm text-muted-foreground cursor-pointer leading-tight"
            >
              Entendo que o módulo será desativado ao final do período de faturamento e desejo
              prosseguir com o agendamento.
            </label>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!confirmed || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Agendando...
              </>
            ) : (
              'Agendar Desativação'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModuleDeactivationDialog;
